import {
  Card,
  Grid,
  TextField,
  MenuItem,
  Select,
  Checkbox,
  FormControl,
  InputLabel,
  Typography,
  Skeleton,
} from "@mui/material";
import WDBox from "../../../../MaterialComponents/WDBox";
import WDButton from "../../../../MaterialComponents/WDButton";
import WDInput from "../../../../MaterialComponents/WDInput";
import WDTypography from "../../../../MaterialComponents/WDTypography";

import { Formik, Form, Field, ErrorMessage } from "formik";
import { object, string, number, date, InferType } from "yup";
import React, { useEffect, useState, useRef } from "react";
import AppService from "../../../../Configurations/Appservice";
import { useFormik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  dismissLoader,
  showLoader,
  showSuccessMessage,
  updateScreenTitle,
} from "../../../../LayoutContainers/AppLayout/redux/AppSlice";

const initialValues = {
  organisationId: "",
  serviceId: "",
  serviceChargeIncTax: "",
  serviceCharge: "",
  govtCharge: "",
  taxOnServiceCharge: "",
};

const validationSchema = object({
  organisationId: string().required("Name is Mandatory"),
  serviceId: string().required("Phone Number is Mandatory"),
  govtCharge: string().required("Mandatory"),
  serviceChargeIncTax: string().required("Mandatory"),
});

const PartnerServiceMapping = () => {
  const [services, setServices] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [isMappingLoading, mappingLoading] = useState(false);
  const [mapping, setMapping] = useState();
  const [serviceDetails, setServiceDetails] = useState(null);
  const [serviceDetailsLoading, setServiceDetailsLoading] = useState(false);
  const [serviceDetailsError, setServiceDetailsError] = useState(null);
  const [chargeMode, setChargeMode] = useState('FIXED');
  const [percentage, setPercentage] = useState('');
  const [isEditing, editing] = useState(false);
  const [preSelectedData, setPreSelectedData] = useState(null);
  const formikRef = useRef();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const calculateServiceAndTaxFromInclusive = (inclusiveAmount) => {
    const inc = parseFloat(inclusiveAmount);
    if (!Number.isFinite(inc)) {
      return { serviceCharge: "", taxOnServiceCharge: "" };
    }

    const rawService = inc / 1.05;
    const serviceCharge = Math.floor(rawService * 100) / 100;
    const tax = inc - serviceCharge;
    const taxOnServiceCharge = Math.round(tax * 100) / 100;

    return {
      serviceCharge: serviceCharge.toFixed(2),
      taxOnServiceCharge: taxOnServiceCharge.toFixed(2),
    };
  };

  const calculateInclusiveFromServiceAndTax = (serviceCharge, taxOnServiceCharge) => {
    const service = parseFloat(serviceCharge);
    const tax = parseFloat(taxOnServiceCharge);
    if (!Number.isFinite(service) || !Number.isFinite(tax)) return "";
    return (service + tax).toFixed(2);
  };

  const applyInclusiveAmount = (inclusiveAmount, setFieldValue) => {
    setFieldValue("serviceChargeIncTax", inclusiveAmount);
    const { serviceCharge, taxOnServiceCharge } = calculateServiceAndTaxFromInclusive(inclusiveAmount);
    setFieldValue("serviceCharge", serviceCharge);
    setFieldValue("taxOnServiceCharge", taxOnServiceCharge);
  };

  console.log('[ServiceMapping] Initial render with location state:', location.state);

  useEffect(() => {
    console.log('[ServiceMapping] Component mounted with location:', location);
    console.log('[ServiceMapping] Location state:', location.state);
    
    // Check if we have pre-selected data from workflow assignment
    const selectedCustomerName = location.state?.selectedCustomerName || sessionStorage.getItem('selectedCustomerName');
    const selectedServiceName = location.state?.selectedServiceName || sessionStorage.getItem('selectedServiceName');
    
    // Log all session storage keys and values
    const sessionStorageData = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      sessionStorageData[key] = sessionStorage.getItem(key);
    }
    
    console.log('[ServiceMapping] Session storage on mount:', sessionStorageData);
    console.log('[ServiceMapping] Pre-selected names:', { selectedCustomerName, selectedServiceName });
    
    // Update screen title with pre-selected information if available
    if (selectedCustomerName && selectedServiceName) {
      console.log('[ServiceMapping] Setting title with pre-selected data');
      dispatch(updateScreenTitle(`Map Service: ${selectedServiceName} for ${selectedCustomerName}`));
      
      // Also show a blue info box at the top of the page
      setTimeout(() => {
        dispatch(showSuccessMessage(`Mapping service "${selectedServiceName}" for "${selectedCustomerName}"`, 'info'));
      }, 500);
    } else {
      console.log('[ServiceMapping] Using default title (no pre-selected data)');
      dispatch(updateScreenTitle("Partner Organisation Charge Mapping"));
    }
    
    return () => dispatch(updateScreenTitle(""));
  }, []);
  
  // Monitor preSelectedData changes and trigger service change handler when needed
  useEffect(() => {
    if (preSelectedData && preSelectedData.organisationId && preSelectedData.serviceId && formikRef.current) {
      console.log('[ServiceMapping] Pre-selected data ready, triggering service change handler');
      
      // Use a timeout to ensure the form is fully rendered
      setTimeout(() => {
        _onServiceChange({ target: { value: preSelectedData.serviceId } }, formikRef.current.setFieldValue);
      }, 300);
    }
  }, [preSelectedData]);
  
  // Direct form initialization from session storage - runs once on mount
  useEffect(() => {
    // This effect runs after the component is mounted
    const directlySetFormValues = () => {
      if (!formikRef.current) return;
      
      const selectedCustomerId = sessionStorage.getItem('selectedCustomerId');
      const selectedServiceId = sessionStorage.getItem('selectedServiceId');
      
      console.log('[ServiceMapping] Direct form initialization attempt with:', {
        selectedCustomerId,
        selectedServiceId,
        formikRef: !!formikRef.current
      });
      
      if (selectedCustomerId) {
        console.log('[ServiceMapping] Directly setting organisationId:', selectedCustomerId);
        formikRef.current.setFieldValue('organisationId', selectedCustomerId);
        
        if (selectedServiceId) {
          // Set service ID with a slight delay to ensure organization ID is set first
          setTimeout(() => {
            console.log('[ServiceMapping] Directly setting serviceId:', selectedServiceId);
            formikRef.current.setFieldValue('serviceId', selectedServiceId);
            
            // Force a manual service change event
            setTimeout(() => {
              console.log('[ServiceMapping] Forcing service change handler');
              _onServiceChange({ target: { value: selectedServiceId } }, formikRef.current.setFieldValue);
            }, 300);
          }, 300);
        }
      }
    };
    
    // Try immediately
    directlySetFormValues();
    
    // And also try after a short delay to ensure form is fully initialized
    setTimeout(directlySetFormValues, 1000);
  }, []);

  useEffect(() => {
    // Check for data from workflow assignment page first
    const fromWorkflowAssignment = location.state?.fromWorkflowAssignment || sessionStorage.getItem('fromWorkflowAssignment') === 'true';
    const selectedCustomerId = location.state?.selectedCustomerId || sessionStorage.getItem('selectedCustomerId');
    const selectedServiceId = location.state?.selectedServiceId || sessionStorage.getItem('selectedServiceId');
    
    // Log all session storage keys and values for debugging
    const sessionStorageData = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      sessionStorageData[key] = sessionStorage.getItem(key);
    }
    
    console.log('[ServiceMapping] Checking for pre-selected data:', {
      fromWorkflowAssignment,
      selectedCustomerId,
      selectedServiceId,
      locationState: location.state,
      sessionStorage: sessionStorageData
    });
    
    // Handle pre-selected data from workflow assignment
    if (fromWorkflowAssignment && selectedCustomerId && selectedServiceId) {
      const selectedCustomerName = location.state?.selectedCustomerName || sessionStorage.getItem('selectedCustomerName');
      const selectedServiceName = location.state?.selectedServiceName || sessionStorage.getItem('selectedServiceName');
      
      console.log('Pre-selected data from workflow assignment:', {
        customerId: selectedCustomerId,
        serviceId: selectedServiceId,
        customerName: selectedCustomerName,
        serviceName: selectedServiceName
      });
      
      // Store the fromWorkflowAssignment flag in session storage
      sessionStorage.setItem('fromWorkflowAssignment', 'true');
      
      // Display a message to the user showing what they're mapping
      if (selectedCustomerName && selectedServiceName) {
        dispatch(showSuccessMessage(`Mapping service "${selectedServiceName}" for "${selectedCustomerName}"`, 'info'));
      }
      
      editing(true);
      dispatch(showLoader());
      const organisationUrl = "v1/organisation/keyValues?type=PARTNER";
      const serviceUrl = "v1/service/keyValues";
      const mappedValueUrl = `v1/serviceMapping?serviceId=${selectedServiceId}&organisationId=${selectedCustomerId}`;
      let endpoints = [organisationUrl, serviceUrl];
      
      Promise.all(endpoints.map((endpoint) => AppService.get(endpoint)))
        .then(([organisations, services]) => {
          setOrganisations(organisations);
          setServices(services);
          
          // Store the pre-selected data for use in the Formik initialValues
          setPreSelectedData({
            organisationId: selectedCustomerId,
            serviceId: selectedServiceId
          });
          
          console.log('[ServiceMapping] Stored pre-selected data:', {
            organisationId: selectedCustomerId,
            serviceId: selectedServiceId,
            organisations: organisations.map(o => ({ id: o.id, name: o.name })),
            services: services.map(s => ({ id: s.id, name: s.name }))
          });
          
          // Check if the values exist in the dropdown options
          const orgExists = organisations.some(o => o.id === selectedCustomerId);
          const svcExists = services.some(s => s.id === selectedServiceId);
          
          console.log(`[ServiceMapping] Selected values exist in dropdowns: org=${orgExists}, service=${svcExists}`);
          
          if (!orgExists || !svcExists) {
            console.error('[ServiceMapping] Selected values not found in dropdown options');
            dispatch(showSuccessMessage('Unable to pre-select customer or service - please select manually', 'warning'));
          }
          
          // Try to get mapping data if it exists
          AppService.get(mappedValueUrl)
            .then(mapping => {
              setMapping(mapping);
              formikRef.current.setFieldValue("govtCharge", mapping.govtCharge || '');
              formikRef.current.setFieldValue("taxOnServiceCharge", mapping.taxOnServiceCharge || '');
              formikRef.current.setFieldValue("serviceCharge", mapping.serviceCharge || '');
              formikRef.current.setFieldValue(
                "serviceChargeIncTax",
                calculateInclusiveFromServiceAndTax(mapping.serviceCharge, mapping.taxOnServiceCharge)
              );
            })
            .catch(() => {
              // No mapping exists yet, set default values
              formikRef.current.setFieldValue("govtCharge", '');
              formikRef.current.setFieldValue("taxOnServiceCharge", '');
              formikRef.current.setFieldValue("serviceCharge", '');
              formikRef.current.setFieldValue("serviceChargeIncTax", '');
            });
        })
        .catch((error) => {
          console.error('Error fetching data:', error);
        })
        .finally(() => {
          dispatch(dismissLoader());
          
          // Don't clear session storage immediately - we'll clear it when the form is submitted
          // This ensures the data is still available if the page is refreshed
        });
      
      return;
    }
    
    // Original code for handling item from location state
    const item = location.state?.item;
    if (!item) {
      return;
    }
    editing(true);
    dispatch(showLoader());
    const organisationUrl = "v1/organisation/keyValues?type=PARTNER";
    const serviceUrl = "v1/service/keyValues";
    const maapedValueUrl = `v1/serviceMapping?serviceId=${item.serviceId}&organisationId=${item.organisationId}`;
    let endpoints = [organisationUrl, serviceUrl, maapedValueUrl];
    Promise.all(endpoints.map((endpoint) => AppService.get(endpoint)))
      .then(([organisations, services, mapping]) => {
        setOrganisations(organisations);
        setServices(services);
        setMapping(mapping);
        formikRef.current.setFieldValue("govtCharge", mapping.govtCharge);
        formikRef.current.setFieldValue("taxOnServiceCharge", mapping.taxOnServiceCharge);
        formikRef.current.setFieldValue("serviceCharge", mapping.serviceCharge);
        formikRef.current.setFieldValue(
          "serviceChargeIncTax",
          calculateInclusiveFromServiceAndTax(mapping.serviceCharge, mapping.taxOnServiceCharge)
        );
        formikRef.current.setFieldValue("serviceId", item.serviceId);
        formikRef.current.setFieldValue("organisationId", item.organisationId);
      })
      .catch((error) => {})
      .finally(() => {
        dispatch(dismissLoader());
      });
  }, []);

  useEffect(() => {
    const item = location.state?.item;
    if (item) {
      return;
    }
    AppService.get("v1/service/keyValues")
      .then((response) => {
        setServices(response);
      })
      .catch((error) => {
        alert("something went wrong");
      });
  }, []);

  useEffect(() => {
    const item = location.state?.item;
    if (item) {
      return;
    }
    const params = { type: "PARTNER" };
    AppService.get("v1/organisation/keyValues", { params })
      .then((response) => {
        setOrganisations(response);
      })
      .catch((error) => {});
  }, []);

  const _submitHandler = (values, { resetForm }) => {
    console.log('[ServiceMapping] Submitting mapping:', values);
    dispatch(showLoader());
    const { serviceChargeIncTax, ...payload } = values;
    AppService.post("v1/serviceMapping", payload)
      .then((response) => {
        resetForm();
        setChargeMode('FIXED');
        setPercentage('');
        
        // Get customer and service names for the success message
        const customerName = organisations.find(org => org.id === values.organisationId)?.name || 'Customer';
        const serviceName = services.find(svc => svc.id === values.serviceId)?.name || 'Service';
        
        dispatch(showSuccessMessage(`Successfully mapped service "${serviceName}" for "${customerName}"`));
        
        // Check if we came from workflow assignment page
        const fromWorkflowAssignment = location.state?.fromWorkflowAssignment || sessionStorage.getItem('fromWorkflowAssignment') === 'true';
        if (fromWorkflowAssignment) {
          console.log('[ServiceMapping] Setting return flags for workflow assignment page');
          // Set flags to indicate successful mapping
          sessionStorage.setItem('returnedFromServiceMapping', 'true');
          sessionStorage.setItem('mappedCustomerId', values.organisationId);
          sessionStorage.setItem('mappedServiceId', values.serviceId);
          
          // Clear the navigation flags after successful mapping
          setTimeout(() => {
            sessionStorage.removeItem('selectedCustomerId');
            sessionStorage.removeItem('selectedCustomerName');
            sessionStorage.removeItem('selectedServiceId');
            sessionStorage.removeItem('selectedServiceName');
            sessionStorage.removeItem('fromWorkflowAssignment');
            
            // Navigate back to workflow assignment page
            navigate(-1); // Go back to previous page
          }, 1500); // Short delay to show success message
        }
      })
      .catch((error) => {
        console.error('Error saving service mapping:', error);
        dispatch(showSuccessMessage("Failed to save service mapping", 'error'));
      })
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  const _renderChargesAreaLoader = () => {
    return (
      <WDBox mt={3}>
        <WDTypography variant="h6">Charge Mapping</WDTypography>
        <Grid container spacing={3} py={2}>
          <Grid item xs={12} md={3}>
            <WDBox>
              <Skeleton
                variant="rounded"
                height={40}
                animation={isMappingLoading ? "pulse" : false}
              />
            </WDBox>
          </Grid>
          <Grid item xs={12} md={3}>
            <WDBox>
              <Skeleton
                variant="rounded"
                height={40}
                animation={isMappingLoading ? "pulse" : false}
              />
            </WDBox>
          </Grid>
          <Grid item xs={12} md={3}>
            <WDBox>
              <Skeleton
                variant="rounded"
                height={40}
                animation={isMappingLoading ? "pulse" : false}
              />
            </WDBox>
          </Grid>
        </Grid>
      </WDBox>
    );
  };

  const _renderChargesArea = (values, errors, isValid, touched, dirty, setFieldValue) => {
    const defaultServiceIncTax = Number.isFinite(
      parseFloat(
        calculateInclusiveFromServiceAndTax(
          serviceDetails?.serviceCharge,
          serviceDetails?.taxOnServiceCharge
        )
      )
    )
      ? parseFloat(
          calculateInclusiveFromServiceAndTax(
            serviceDetails?.serviceCharge,
            serviceDetails?.taxOnServiceCharge
          )
        )
      : null;

    const mappedServiceIncTax = Number.isFinite(parseFloat(values?.serviceChargeIncTax))
      ? parseFloat(values?.serviceChargeIncTax)
      : null;

    const thaklisProfit =
      defaultServiceIncTax !== null && mappedServiceIncTax !== null
        ? defaultServiceIncTax - mappedServiceIncTax
        : null;

    return (
      <WDBox mt={3}>
        <WDTypography variant="h6">Charge Mapping</WDTypography>
        {(serviceDetailsError || serviceDetails) && (
          <WDBox mt={2} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            <WDTypography variant="subtitle2" fontWeight="bold" mb={1}>
              Default Service Charges
            </WDTypography>
            {serviceDetailsError ? (
              <WDTypography variant="caption" color="error">
                {serviceDetailsError}
              </WDTypography>
            ) : (
              <>
                <WDTypography variant="caption" display="block">
                  Govt: AED {parseFloat(serviceDetails?.govtCharge || 0).toFixed(2)}
                </WDTypography>
                <WDTypography variant="caption" display="block">
                  Service (Excl. Tax): AED {parseFloat(serviceDetails?.serviceCharge || 0).toFixed(2)}
                </WDTypography>
                <WDTypography variant="caption" display="block">
                  Tax: AED {parseFloat(serviceDetails?.taxOnServiceCharge || 0).toFixed(2)}
                </WDTypography>
                <WDTypography variant="caption" display="block" fontWeight="bold">
                  Service (Incl. Tax): AED {parseFloat(calculateInclusiveFromServiceAndTax(serviceDetails?.serviceCharge, serviceDetails?.taxOnServiceCharge) || 0).toFixed(2)}
                </WDTypography>
              </>
            )}
          </WDBox>
        )}
        <Grid container spacing={3} py={2}>
          <Grid item xs={12} md={3}>
            <WDBox>
              <Field
                name="govtCharge"
                label="Govt Charge"
                fullWidth
                as={TextField}
                error={Boolean(errors.govtCharge) && Boolean(touched.govtCharge)}
                helperText={Boolean(touched.govtCharge) && errors.govtCharge}
              />
            </WDBox>
          </Grid>
          <Grid item xs={12} md={3}>
            <WDBox>
              <TextField
                select
                label="Charge Type"
                fullWidth
                value={chargeMode}
                onChange={(e) => {
                  const next = e.target.value;
                  setChargeMode(next);
                  setPercentage('');
                  if (next === 'PERCENTAGE') {
                    setFieldValue("serviceChargeIncTax", '');
                    setFieldValue("serviceCharge", '');
                    setFieldValue("taxOnServiceCharge", '');
                  }
                }}
              >
                <MenuItem value="FIXED">FIXED</MenuItem>
                <MenuItem value="PERCENTAGE">PERCENTAGE</MenuItem>
              </TextField>
            </WDBox>
          </Grid>

          {chargeMode === 'PERCENTAGE' && (
            <Grid item xs={12} md={3}>
              <WDBox>
                <TextField
                  label="Percentage"
                  fullWidth
                  value={percentage}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
                    setPercentage(value);

                    if (!serviceDetails) return;
                    const defaultInc = calculateInclusiveFromServiceAndTax(serviceDetails.serviceCharge, serviceDetails.taxOnServiceCharge);
                    const p = parseFloat(value);
                    const base = parseFloat(defaultInc);
                    if (!Number.isFinite(p) || !Number.isFinite(base)) return;
                    const computedInc = (base * (p / 100)).toFixed(2);
                    applyInclusiveAmount(computedInc, setFieldValue);
                  }}
                  disabled={serviceDetailsLoading || !serviceDetails}
                  helperText={!serviceDetails ? 'Default service charges are required for percentage calculation' : 'Calculated from default service charge (incl. tax)'}
                />
              </WDBox>
            </Grid>
          )}

          <Grid item xs={12} />

          <Grid item xs={12} md={3}>
            <WDBox>
              <Field
                name="serviceChargeIncTax"
                label="Service Charge"
                fullWidth
                as={TextField}
                onChange={(e) => {
                  const value = e.target.value;
                  if (chargeMode === 'PERCENTAGE') return;
                  setFieldValue("serviceChargeIncTax", value);
                  const { serviceCharge, taxOnServiceCharge } = calculateServiceAndTaxFromInclusive(value);
                  setFieldValue("serviceCharge", serviceCharge);
                  setFieldValue("taxOnServiceCharge", taxOnServiceCharge);
                }}
                disabled={chargeMode === 'PERCENTAGE'}
                error={Boolean(errors.serviceChargeIncTax) && Boolean(touched.serviceChargeIncTax)}
                helperText={
                  (Boolean(touched.serviceChargeIncTax) && errors.serviceChargeIncTax) ||
                  (chargeMode === 'PERCENTAGE' ? "Calculated (including tax)" : "Including tax")
                }
              />
            </WDBox>
          </Grid>
          <Grid item xs={12} md={3}>
            <WDBox>
              <Field
                name="taxOnServiceCharge"
                label="Tax On Service Charge"
                fullWidth
                as={TextField}
                disabled
                error={
                  Boolean(errors.taxOnServiceCharge) &&
                  Boolean(touched.taxOnServiceCharge)
                }
                helperText={
                  Boolean(touched.taxOnServiceCharge) &&
                  errors.taxOnServiceCharge
                }
              />
            </WDBox>
          </Grid>
          <Grid item xs={12} md={3}>
            <WDBox>
              <Field
                name="serviceCharge"
                label="Service Charge (Excl. Tax)"
                fullWidth
                as={TextField}
                disabled
                error={
                  Boolean(errors.serviceCharge) &&
                  Boolean(touched.serviceCharge)
                }
                helperText={
                  Boolean(touched.serviceCharge) && errors.serviceCharge
                }
              />
            </WDBox>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <WDBox mt={1} p={1} sx={{ backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <WDTypography
                variant="subtitle2"
                color={thaklisProfit === null ? "text.primary" : (thaklisProfit >= 0 ? "success.main" : "error.main")}
                fontWeight="bold"
              >
                Thaklis Profit: AED {thaklisProfit === null ? '-' : thaklisProfit.toFixed(2)}
              </WDTypography>
            </WDBox>
          </Grid>
        </Grid>
      </WDBox>
    );
  };

  const _onServiceChange = (event, setFieldValue) => {
    const serviceId = event.target.value;
    console.log('[ServiceMapping] Service changed:', serviceId);

    setChargeMode('FIXED');
    setPercentage('');
    
    // Update the serviceId field in the form
    setFieldValue("serviceId", serviceId);

    // Clear previous mapped values immediately to avoid stale profit/mismatch while loading new data
    setFieldValue("govtCharge", '');
    setFieldValue("taxOnServiceCharge", '');
    setFieldValue("serviceCharge", '');
    setFieldValue("serviceChargeIncTax", '');
    setMapping(undefined);
    
    // Don't proceed if no service ID or organization ID
    if (!serviceId || !formikRef.current.values.organisationId) {
      console.log('[ServiceMapping] Missing serviceId or organisationId, skipping mapping fetch');
      return;
    }

    setServiceDetailsError(null);
    setServiceDetails(null);
    setServiceDetailsLoading(true);
    AppService.get(`v1/service/details?serviceId=${serviceId}`)
      .then((details) => {
        setServiceDetails(details);
      })
      .catch((e) => {
        console.error('[ServiceMapping] Failed to load service details:', e);
        setServiceDetails(null);
        setServiceDetailsError('Unable to load default charges for this service.');
      })
      .finally(() => {
        setServiceDetailsLoading(false);
      });

    mappingLoading(true);
    const params = {
      serviceId,
      organisationId: formikRef.current.values.organisationId,
    };
    
    console.log('[ServiceMapping] Fetching mapping data with params:', params);
    
    AppService.get("v1/serviceMapping", { params })
      .then((response) => {
        console.log('[ServiceMapping] Mapping data received:', response);
        setMapping(response);
        setFieldValue("govtCharge", response.govtCharge || '');
        setFieldValue("taxOnServiceCharge", response.taxOnServiceCharge || '');
        setFieldValue("serviceCharge", response.serviceCharge || '');
        setFieldValue(
          "serviceChargeIncTax",
          calculateInclusiveFromServiceAndTax(response.serviceCharge, response.taxOnServiceCharge)
        );
      })
      .catch((error) => {
        console.error('[ServiceMapping] Error fetching mapping data:', error);
        // Clear mapping data if there was an error
        setMapping({});
        setFieldValue("govtCharge", '');
        setFieldValue("taxOnServiceCharge", '');
        setFieldValue("serviceCharge", '');
        setFieldValue("serviceChargeIncTax", '');
      })
      .finally(() => {
        mappingLoading(false);
      });
  };

  // Get pre-selected names for display
  const selectedCustomerName = location.state?.selectedCustomerName || sessionStorage.getItem('selectedCustomerName');
  const selectedServiceName = location.state?.selectedServiceName || sessionStorage.getItem('selectedServiceName');
  const fromWorkflowAssignment = location.state?.fromWorkflowAssignment || sessionStorage.getItem('fromWorkflowAssignment') === 'true';
  
  // Determine the title based on whether we have pre-selected data
  const pageTitle = fromWorkflowAssignment && selectedCustomerName && selectedServiceName
    ? `Map Service: ${selectedServiceName} for ${selectedCustomerName}`
    : "Partner Organisation Charge Mapping";
  
  return (
    <WDBox>
      <Card sx={{ padding: 3 }}>
        {fromWorkflowAssignment && selectedCustomerName && selectedServiceName && (
          <WDBox mb={2} p={2} sx={{ backgroundColor: '#e3f2fd', borderRadius: '4px' }}>
            <WDTypography variant="subtitle1" fontWeight="bold">
              Mapping service "{selectedServiceName}" for "{selectedCustomerName}"
            </WDTypography>
          </WDBox>
        )}
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={_submitHandler}
          enableReinitialize={false}
        >
          {({ values, errors, isValid, touched, dirty, setFieldValue }) => (
            <Form>
              <WDBox>
                <WDTypography variant="h5">{pageTitle}</WDTypography>
              </WDBox>
              <WDBox my={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <WDBox>
                      <FormControl fullWidth>
                        <InputLabel id="service-label">Partner Orgnisation</InputLabel>
                        <Field
                          name="organisationId"
                          label="Partner Orgnisation"
                          fullWidth
                          as={Select}
                          onChange={(e) => {
                            const nextOrgId = e.target.value;
                            setChargeMode('FIXED');
                            setPercentage('');
                            setServiceDetailsError(null);
                            setServiceDetails(null);
                            setFieldValue('organisationId', nextOrgId);
                            setFieldValue('serviceId', '');
                            setFieldValue('govtCharge', '');
                            setFieldValue('taxOnServiceCharge', '');
                            setFieldValue('serviceCharge', '');
                            setFieldValue('serviceChargeIncTax', '');
                            setMapping(undefined);
                          }}
                          error={Boolean(errors.organisationId) && Boolean(touched.organisationId)}
                          helperText={Boolean(touched.organisationId) && errors.organisationId}
                          disabled={isEditing}
                        >
                          {organisations.map((item) => (
                            <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </WDBox>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <WDBox>
                      <FormControl fullWidth>
                        <InputLabel id="service-label">Service</InputLabel>
                        <Field
                          name="serviceId"
                          label="Service"
                          fullWidth
                          as={Select}
                          disabled={!values.organisationId || isEditing}
                          onChange={(e) => _onServiceChange(e, setFieldValue)}
                          error={Boolean(errors.serviceId) && Boolean(touched.serviceId)}
                          helperText={Boolean(touched.serviceId) && errors.serviceId}
                          isLoading={true}
                        >
                          {services.map((item) => (
                            <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </WDBox>
                  </Grid>
                </Grid>
                {!mapping || isMappingLoading
                  ? _renderChargesAreaLoader()
                  : _renderChargesArea(values, errors, isValid, touched, dirty, setFieldValue)}
              </WDBox>
              <WDBox display="flex" justifyContent="flex-end" pt={3}>
                <WDButton variant="gradient" disabled={!dirty || !isValid} type="submit">
                  {mapping?.existing ? "UPDATE" : "SUBMIT"}
                </WDButton>
              </WDBox>
            </Form>
          )}
        </Formik>
      </Card>
    </WDBox>
  );
};

export default PartnerServiceMapping;
